import { SHOP_DETAIL_VIEW_TYPE_INTERIOR } from '@/constants';
import useDisplayComponent from '@/hooks/useDisplayComponent';
import TrackingService from '@/services/tracking';
import { theme } from '@/styles';
import { ShopDetailViewInfo } from '@/types';
import Image from '@/ui/Image';

import SlideBox from '@/components/SlideBox';
import { getAsset } from '@/utils';
import { ApiPagingResponseData } from '@remodeling/base';
import { ConstructionCaseContent } from '@remodeling/content';
import React from 'react';
import styled from 'styled-components';
import AptRank from './AptRank';
import Badges from './Badges';
import ConstructionCase from './Construction';
import Consultant from './Consultant';
import Preview from './Preview';
import {
  INFORMED_SHOP_DETAIL_INTERIOR_AGENT,
  TYPE_VALUE_PC,
} from '@display-component/shared';
import InformedTemplate from '@/components/InformedTemplate';

interface AgencyContentProps {
  data: ShopDetailViewInfo;
  constructData?: ApiPagingResponseData<ConstructionCaseContent>;
  handleConstructionPopup: () => void;
  isLoading: {
    detailDataState: boolean;
    constructDataState: boolean;
  };
  dispTemplateNo?: string;
}

const AgencyContent = ({
  data,
  constructData,
  handleConstructionPopup,
  isLoading,
  dispTemplateNo,
}: AgencyContentProps) => {
  const { handleLink } = useDisplayComponent();

  return (
    <Container {...TrackingService.tagGTM('agent_rh_kb')}>
      <ImageBox>
        {data?.header && (
          <SlideBox
            baseHeight={260}
            baseWidth={770}
            data={data?.header.mainImages}
            hasPagination
            arrowType="lg"
            hasInfinite
            hasArrow
          >
            {({ width, height, item, index }) => (
              <Image
                src={`http:${item?.url}`}
                alt={`eventRollingBanner${index}`}
                width={width}
                height={height}
                objectFit="cover"
              />
            )}
          </SlideBox>
        )}
      </ImageBox>
      {isLoading.constructDataState && isLoading.detailDataState ? (
        <>
          <ContentContainer>
            {data?.header?.description && (
              <>
                <TitleContainer>
                  <Title>매장 소개</Title>
                </TitleContainer>
                <Description>{data?.header?.description}</Description>
              </>
            )}
          </ContentContainer>
          {data.viewType === SHOP_DETAIL_VIEW_TYPE_INTERIOR && (
            <BannerBox>
              <InformedTemplate
                handleLink={handleLink}
                module={INFORMED_SHOP_DETAIL_INTERIOR_AGENT}
                typeValue={TYPE_VALUE_PC}
                channelCd="PC"
              />
            </BannerBox>
          )}
          {!!data?.agentRhkb?.images && data?.agentRhkb?.images?.length > 0 && (
            <Preview data={data?.agentRhkb?.images} title="미리보기" />
          )}
          {!!data?.agentInterior?.images &&
            data?.agentInterior?.images?.length > 0 && (
              <Preview data={data?.agentInterior?.images} title="미리보기" />
            )}
          {!!constructData && constructData.totalElements > 0 && (
            <ConstructionCase
              constructData={constructData}
              handleConstructionPopup={handleConstructionPopup}
            />
          )}
          {!!data?.agentInterior?.consultants &&
            data?.agentInterior?.consultants?.length > 0 &&
            data?.agentInterior?.consultantShowYn === 'Y' && (
              <Consultant
                data={data?.agentInterior?.consultants}
                title="전문 상담사"
              />
            )}
          {!!data?.agentRhkb?.consultants &&
            data?.agentRhkb?.consultants?.length > 0 &&
            data?.agentRhkb?.consultantShowYn === 'Y' && (
              <Consultant
                data={data?.agentRhkb?.consultants}
                title="전문 상담사"
              />
            )}
          {!!data?.agentRhkb?.apartRanks &&
            data?.agentRhkb?.apartRanks?.length > 0 && (
              <AptRank
                title={`최다 시공 아파트`}
                data={data?.agentRhkb?.apartRanks}
              />
            )}
          {!!data?.agentRhkb?.badges && data?.agentRhkb?.badges?.length > 0 && (
            <Badges
              title="시공 고객의 평가"
              data={data?.agentRhkb?.badges.slice(0, 2)}
            />
          )}
          {!data?.header?.description &&
            (!data?.agentRhkb?.images ||
              data?.agentRhkb?.images?.length === 0) &&
            (!data?.agentRhkb?.consultants ||
              data?.agentRhkb?.consultants?.length === 0) &&
            (!data?.agentRhkb?.apartRanks ||
              data?.agentRhkb?.apartRanks?.length === 0) &&
            (!constructData || constructData.totalElements === 0) && (
              <ErrorBox>
                <BangIconBox>
                  <Image
                    src={getAsset('/assets/icons/ic_empty_72.svg')}
                    width="72px"
                    height="72px"
                    alt="errorBangIcon"
                  />
                </BangIconBox>
                <ErrorText>매장 소개 준비 중입니다</ErrorText>
              </ErrorBox>
            )}
        </>
      ) : (
        <LoadingBox>
          <Image
            src={getAsset('/assets/icons/loading-obj-new.gif')}
            height={'40px'}
            width="40px"
            alt="loadingIcon"
          />
        </LoadingBox>
      )}
    </Container>
  );
};

export default AgencyContent;

const Container = styled.div`
  width: 100%;
`;

const ImageBox = styled.div`
  margin-bottom: 40px;
`;

const ContentContainer = styled.div`
  min-height: 24px;
`;

const TitleContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
`;

const Title = styled.div`
  display: block;
  color: ${theme.colors.accent_colors.primary.default};
  ${theme.typography.subtitle4};
`;

const Description = styled.div`
  display: block;
  color: ${theme.colors.neutral_colors.gray[700]};
  ${theme.typography.body1};
  margin-bottom: 80px;
`;

const BannerBox = styled.div`
  width: 100%;
  margin-bottom: 80px;
`;

const ErrorBox = styled.div`
  display: flex;
  height: 100%;
  min-height: 318px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const BangIconBox = styled.div`
  width: 72px;
  height: 72px;
`;

const ErrorText = styled.div`
  color: ${theme.colors.accent_colors.primary.default};
  ${theme.typography.body1};
`;

const LoadingBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
`;
